import React from "react";
import { Text } from "@chakra-ui/react";
import { Module, DB_COLLECTION_NAMES } from "@constants";
import { query, collection, getDocs, where } from "firebase/firestore";
import { FirebaseContext } from "@contexts";

import { useStoreTags } from "@hooks";
import { PRODUCTION_STEPS_PS, PRODUCTION_STEPS_2S } from "@constants";

import { JsonForm, JsonFormControl } from "../JsonForm";

import { ButtonModal } from "../ModalWrappers";

function download(filename: string, text: string) {
  const element = document.createElement("a");
  element.setAttribute(
    "href",
    "data:text/plain;charset=utf-8," + encodeURIComponent(text),
  );
  element.setAttribute("download", filename);

  element.style.display = "none";
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

function makeDownloadDataControl() {
  return [] as JsonFormControl[];
}

type MakeInventoryReportProps = {
  modulesForBaseplateSteps: string[][];
  modulesFor2SSteps: string[][];
  modulesForPSSSteps: string[][];
  modulesForMapsaSteps: string[][];
};

export function MakeInventoryReportsModal({
  modulesForBaseplateSteps,
  modulesFor2SSteps,
  modulesForPSSSteps,
  modulesForMapsaSteps,
}: MakeInventoryReportProps) {
  const [error] = React.useState("");
  const tags = useStoreTags();

  const tagFormControl = React.useMemo(() => makeDownloadDataControl(), [tags]);

  const { firestore } = React.useContext(FirebaseContext);
  const collectionName = DB_COLLECTION_NAMES.MODULES;
  const collectionRef = collection(firestore, collectionName);
  const badTags = useStoreTags();

  // Warning: these need to be changed in the MakeReports page as well!
  // Note: For now, I'm not doing any additional parsing of the module name, but I have broken this into different components
  // in case this needs to change (i.e. if there are steps with more than one previous step)
  const stepListBaseplates = [
    PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_KAPTON_GLUING,
    PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_BASEPLATE_GLUING,
  ];
  const stepListPSS = [
    PRODUCTION_STEPS_PS.STEP_PS_GLUE_HV_TAIL_TO_SENSOR,
    PRODUCTION_STEPS_PS.STEP_PS_ENCAPSULATE_HV_TAIL,
    PRODUCTION_STEPS_PS.STEP_PS_SENSOR_IV_MEASURMENT,
    PRODUCTION_STEPS_PS.STEP_PS_SENSOR_SPACER_GLUING,
  ];
  const stepListMapsa = [
    PRODUCTION_STEPS_PS.STEP_PS_TEST_MA_PSA,
    PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_BASEPLATE_GLUING,
  ];
  const stepList2S = [
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_KAPTON_GLUING,
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_HV_TAIL_GLUING,
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_BOND_AND_ENCAPSULATE_HV_TAIL,
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_IV_MEASURMENT,
    PRODUCTION_STEPS_2S.STEP_2S_GLUE_SANDWICH,
  ];

  async function test(count: number, moduleId: string) {
    const queryResult = query(collectionRef, where("id", "==", moduleId));

    const allModules = await getDocs(queryResult);
    allModules.forEach((doc) => {
      const module = doc.data() as Module;

      let hasBadTag = false;
      (module.tags ?? []).forEach((tag) => {
        if (badTags.includes(tag)) {
          hasBadTag = true;
        }
      });

      if (hasBadTag) {
        count = count - 1;
      }
    });
    return count;
  }

  async function newTest(
    countArray: number[],
    modulesForSteps: string[][],
    myString: string,
    sensorString: string,
    stepList: string[],
  ) {
    for (let stepNum = 0; stepNum < modulesForSteps.length; stepNum++) {
      let count = countArray[stepNum];
      for (const moduleId of modulesForSteps[stepNum]) {
        count = await test(count, moduleId);
      }
      myString =
        myString + sensorString + " " + stepList[stepNum] + " " + count + "\n";
    }
    return myString;
  }

  const doSubmit = React.useCallback(async () => {
    let dbString = "";

    const countArrayBaseplate = new Array<number>(stepListBaseplates.length);
    for (let i = 0; i < stepListBaseplates.length; i++) {
      countArrayBaseplate[i] = modulesForBaseplateSteps[i].length;
    }
    dbString = await newTest(
      countArrayBaseplate,
      modulesForBaseplateSteps,
      dbString,
      "PS-Baseplate",
      stepListBaseplates,
    );

    const countArrayPSS = new Array<number>(stepListPSS.length);
    for (let i = 0; i < stepListPSS.length; i++) {
      countArrayPSS[i] = modulesForPSSSteps[i].length;
    }
    dbString = await newTest(
      countArrayPSS,
      modulesForPSSSteps,
      dbString,
      "PS-Sensor",
      stepListPSS,
    );

    const countArray2S = new Array<number>(stepList2S.length);
    for (let i = 0; i < stepList2S.length; i++) {
      countArray2S[i] = modulesFor2SSteps[i].length;
    }
    dbString = await newTest(
      countArray2S,
      modulesFor2SSteps,
      dbString,
      "2S-Sensor",
      stepList2S,
    );

    const countArrayMapsa = new Array<number>(stepListMapsa.length);
    for (let i = 0; i < stepListMapsa.length; i++) {
      countArrayMapsa[i] = modulesForMapsaSteps[i].length;
    }
    dbString = await newTest(
      countArrayMapsa,
      modulesForMapsaSteps,
      dbString,
      "MaPSA",
      stepListMapsa,
    );

    download("inventoryReport.csv", dbString);
  }, []);

  return (
    <ButtonModal
      label="Download Inventory Report"
      title="Download Inventory Report"
      size="sm"
    >
      <JsonForm fields={tagFormControl} onSubmit={doSubmit} clearAfterSubmit />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
